import { CrownFilled, CrownOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Typography, message } from "antd";
import { isDatasetGolden } from "common/goldenNominal";
import { useAppDispatch, useAppSelector } from "hooks";
import { useEffect, useRef, useState } from "react";
import { denominateGoldenNominalRequest, nominateGoldenNominalRequest } from "store/goldenNominal/actions";

interface IProps {
    businessAreaId?: string;
    schemaId?: string;
    schemaName?: string;
    dataSetId?: string;
    dataSetName?: string;
    canEdit?: boolean;
    // Backend flag on the loaded dataset; combined with any in-session override.
    isGoldenNominal?: boolean;
}

// Footer control on the Dataset detail: "Nominate as Golden Nominal" (with an
// explainer modal) when not golden, or a "Golden Nominal ✓" toggle (de-nominate) when
// it is. One golden dataset per schema — nominating replaces any prior one.
export default function DatasetGoldenNominalAction({ businessAreaId, schemaId, schemaName, dataSetId, dataSetName, canEdit, isGoldenNominal }: IProps) {
    const dispatch = useAppDispatch();
    const goldenBySchema = useAppSelector((s) => s.goldenNominal?.goldenBySchema);
    const nominateResult = useAppSelector((s) => s.goldenNominal?.nominateResult);
    const denominateResult = useAppSelector((s) => s.goldenNominal?.denominateResult);
    const [modalOpen, setModalOpen] = useState(false);
    const [busy, setBusy] = useState<null | "nominate" | "denominate">(null);
    const nominateBaseline = useRef(nominateResult);
    const denominateBaseline = useRef(denominateResult);

    useEffect(() => {
        if (busy !== "nominate" || nominateResult === nominateBaseline.current) {
            return;
        }
        setBusy(null);
        setModalOpen(false);
        if (nominateResult?.success) {
            message.success("Nominated as the Golden Nominal dataset for this schema.");
        } else {
            message.error(nominateResult?.message || "Nomination failed.");
        }
    }, [nominateResult, busy]);

    useEffect(() => {
        if (busy !== "denominate" || denominateResult === denominateBaseline.current) {
            return;
        }
        setBusy(null);
        if (denominateResult?.success) {
            message.success("Removed the Golden Nominal nomination.");
        } else {
            message.error(denominateResult?.message || "Could not remove the nomination.");
        }
    }, [denominateResult, busy]);

    if (!dataSetId || !schemaId) {
        return null;
    }

    const isGolden = isDatasetGolden(goldenBySchema, schemaId, dataSetId, isGoldenNominal);
    const priorGoldenElsewhere = !!goldenBySchema?.[schemaId] && goldenBySchema[schemaId] !== dataSetId;

    const doNominate = () => {
        nominateBaseline.current = nominateResult;
        setBusy("nominate");
        dispatch(nominateGoldenNominalRequest(businessAreaId, schemaId, dataSetId));
    };

    const doDenominate = () => {
        Modal.confirm({
            title: "Remove Golden Nominal nomination?",
            content: "This dataset will no longer be the survivorship authority for its schema. Downstream dedup/matching will stop preferring its records.",
            okText: "Remove nomination",
            okButtonProps: { danger: true },
            cancelText: "Cancel",
            onOk: () => {
                denominateBaseline.current = denominateResult;
                setBusy("denominate");
                dispatch(denominateGoldenNominalRequest(businessAreaId, schemaId, dataSetId));
            },
        });
    };

    if (isGolden) {
        return (
            <Button
                onClick={doDenominate}
                loading={busy === "denominate"}
                icon={<CrownFilled className="text-amber-500" />}
                className="border-amber-300 bg-amber-50 text-amber-700 hover:!border-amber-400 hover:!text-amber-800"
                data-testid="golden-nominal-toggle"
            >
                Golden Nominal ✓
            </Button>
        );
    }

    if (!canEdit) {
        return null;
    }

    return (
        <>
            <Button onClick={() => setModalOpen(true)} icon={<CrownOutlined />} data-testid="golden-nominal-nominate">
                Nominate as Golden Nominal
            </Button>
            <Modal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={doNominate}
                okText="Confirm — Nominate as Golden Nominal"
                okButtonProps={{ loading: busy === "nominate", icon: <CrownFilled />, className: "!bg-amber-500 !border-amber-500 hover:!bg-amber-600" }}
                title={
                    <span>
                        <CrownFilled className="mr-2 text-amber-500" />
                        Nominate as Golden Nominal Dataset
                    </span>
                }
                data-testid="golden-nominal-modal"
            >
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <Typography.Text strong>What is a Golden Nominal Dataset?</Typography.Text>
                    <Typography.Paragraph className="!mb-2 !mt-1">
                        The Golden Nominal dataset is the <strong>definitive authority</strong> for the <strong>{schemaName || "selected"}</strong> schema. It will be used as the primary
                        source for:
                    </Typography.Paragraph>
                    <ul className="mb-0 list-disc pl-5 text-sm">
                        <li>
                            <strong>Survivorship</strong> — records from this dataset take precedence when deduplicating.
                        </li>
                        <li>
                            <strong>Persistence</strong> — data written back to source systems originates from this dataset.
                        </li>
                        <li>
                            <strong>Reference</strong> — downstream journeys and integrations resolve conflicts against this dataset.
                        </li>
                    </ul>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="rounded border border-gray-100 bg-gray-50 p-2">
                        <div className="text-xs text-gray-500">Schema</div>
                        <div className="font-mono text-sm">{schemaName || "—"}</div>
                    </div>
                    <div className="rounded border border-amber-100 bg-amber-50 p-2">
                        <div className="text-xs text-gray-500">Dataset to nominate</div>
                        <div className="font-mono text-sm">{dataSetName || dataSetId}</div>
                    </div>
                </div>
                {priorGoldenElsewhere && (
                    <Alert
                        className="mt-3"
                        type="warning"
                        showIcon
                        message="Another dataset is currently the Golden Nominal for this schema."
                        description="Confirming will move the nomination to this dataset (one golden dataset per schema)."
                    />
                )}
            </Modal>
        </>
    );
}
